import React, { useMemo, useState, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import { TreeDataModule } from "ag-grid-enterprise";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

ModuleRegistry.registerModules([TreeDataModule]);

const ColoredDot = ({ color }) => (
    <span
        style={{
            display: "inline-block",
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: color,
            marginRight: 6,
        }}
    />
);

const StatusPill = ({ status }) => {
    const bg = status === "Paid" ? "#f9c5b0" : "#f9d57b";
    const color = status === "Paid" ? "#ad4800" : "#5c3b00";
    return (
        <span
            style={{
                background: bg,
                color,
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: "nowrap",
            }}
        >
            {status}
        </span>
    );
};

const StatusDropdown = ({ value, data, api, colDef }) => {
    const [status, setStatus] = useState(value);
    const onChange = (e) => {
        setStatus(e.target.value);
        data.status = e.target.value;
        api.applyTransaction({ update: [data] });
    };
    return (
        <select
            value={status}
            onChange={onChange}
            style={{ padding: "4px 8px", borderRadius: 4 }}
        >
            <option>Paid</option>
            <option>Pending</option>
            <option>Partially Paid</option> {/* added to match your screenshot */}
        </select>
    );
};

const DateInput = ({ value, data, api }) => {
    const [date, setDate] = useState(value || "");
    const onChange = (e) => {
        setDate(e.target.value);
        data.transactionDate = e.target.value;
        api.applyTransaction({ update: [data] });
    };
    return (
        <input
            type="date"
            value={date}
            onChange={onChange}
            style={{ padding: 4, width: 130, borderRadius: 4, border: "1px solid #ccc" }}
        />
    );
};

const TextInput = ({ value, data, api }) => {
    const [val, setVal] = useState(value || "");
    const onChange = (e) => {
        setVal(e.target.value);
        data.transactionId = e.target.value;
        api.applyTransaction({ update: [data] });
    };
    return (
        <input
            type="text"
            value={val}
            onChange={onChange}
            placeholder="TXN Ref"
            style={{ padding: 4, width: 100, borderRadius: 4, border: "1px solid #ccc" }}
        />
    );
};

const WalletDropdown = ({ value, data, api }) => {
    const [wallet, setWallet] = useState(value || "Wallet A");
    const onChange = (e) => {
        setWallet(e.target.value);
        data.wallet = e.target.value;
        api.applyTransaction({ update: [data] });
    };
    return (
        <select
            value={wallet}
            onChange={onChange}
            style={{ padding: "4px 8px", borderRadius: 4 }}
        >
            <option>Wallet A</option>
            <option>Wallet B</option>
        </select>
    );
};

const CheckmarkButton = () => {
    return (
        <button
            style={{
                backgroundColor: "#4caf50",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: 24,
                height: 24,
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: 16,
                lineHeight: "20px",
                textAlign: "center",
            }}
            title="Confirm"
        >
            ✓
        </button>
    );
};

export default function ReferralAndEarningsListv1() {
    // Now the summary row is the root with path length 1
    const rowData = useMemo(() => [
        {
            // This is the top summary row - root node
            path: ["Sarah Mitchell"],
            level: "Summary",
            amount: 72,
            status: "Partially Paid",
            transactionId: "",
            transactionDate: "",
            wallet: "",
            dotColor: "transparent",
            isSummary: true,
        },

        // Children of the summary row
        {
            path: ["Sarah Mitchell", "John Davis"],
            level: "L1",
            amount: 45,
            status: "Paid",
            transactionId: "TXN-001",
            transactionDate: "2025-02-01",
            wallet: "Wallet A",
            dotColor: "green",
        },
        {
            path: ["Sarah Mitchell", "John Davis", "Alex Chen"],
            level: "L2",
            amount: 18,
            status: "Paid",
            transactionId: "TXN-002",
            transactionDate: "2025-03-01",
            wallet: "Wallet A",
            dotColor: "blue",
        },
        {
            path: ["Sarah Mitchell", "John Davis", "Alex Chen", "Kevin Brown"],
            level: "L3",
            amount: 9,
            status: "Paid",
            transactionId: "TXN-003",
            transactionDate: "2025-04-01",
            wallet: "Wallet A",
            dotColor: "blue",
        },
        {
            path: ["Sarah Mitchell", "John Davis", "Smith Lee"],
            level: "L2",
            amount: 18,
            status: "Pending",
            transactionId: "",
            transactionDate: "",
            wallet: "",
            dotColor: "orange",
        },
        {
            path: ["Sarah Mitchell", "John Davis", "Smith Lee", "Mike Adams"],
            level: "L3",
            amount: 9,
            status: "Pending",
            transactionId: "",
            transactionDate: "",
            wallet: "",
            dotColor: "blue",
        },
    ], []);

    const columnDefs = useMemo(() => [
        {
            field: "level",
            headerName: "Level",
            width: 90,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return null;
                return <span>{params.value}</span>;
            },
        },
        {
            field: "amount",
            headerName: "Commission Amount",
            width: 180,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return `$${params.value.toFixed(2)}`;
                return `$${params.value.toFixed(2)}`;
            },
        },
        {
            field: "status",
            headerName: "Payment Status",
            width: 160,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return <StatusPill status={params.value} />;
                return <StatusPill status={params.value} />;
            },
            cellEditor: StatusDropdown,
            editable: true,
        },
        {
            headerName: "Transaction ID",
            field: "transactionId",
            width: 140,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return null;
                return <TextInput {...params} />;
            },
            editable: true,
        },
        {
            headerName: "Transaction Date",
            field: "transactionDate",
            width: 150,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return null;
                return <DateInput {...params} />;
            },
            editable: true,
        },
        {
            headerName: "Wallet",
            field: "wallet",
            width: 140,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return null;
                return <WalletDropdown {...params} />;
            },
            editable: true,
        },
        {
            headerName: "",
            field: "checkmark",
            width: 70,
            cellRenderer: (params) => {
                if (params.data?.isSummary) return null;
                return <CheckmarkButton />;
            },
        },
    ], []);

    const autoGroupColumnDef = useMemo(() => ({
        headerName: "User Name",
        minWidth: 280,
        cellRenderer: "agGroupCellRenderer",
        cellRendererParams: {
            suppressCount: true,
            innerRenderer: (params) => {
                if (params.data?.isSummary) {
                    return (
                        <div style={{ fontWeight: 600, fontSize: 16 }}>
                            {params.value}
                        </div>
                    );
                }

                const levelColorMap = {
                    Summary: "transparent",
                    L1: "green",
                    L2: "blue",
                    L3: "blue",
                    default: "orange",
                };
                const dotColor = levelColorMap[params.data.level] || levelColorMap.default;

                const referredBy =
                    params.node.level > 1
                        ? params.data.path[params.node.level - 1]
                        : null;

                return (
                    <div style={{ fontWeight: params.node.level === 1 ? 600 : 400, lineHeight: "1.2em" }}>
                        <ColoredDot color={dotColor} />
                        <div style={{ display: "inline-block" }}>
                            <div>{params.value}</div>
                            {referredBy && (
                                <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                                    Referred by: {referredBy}
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
    }), []);

    const getDataPath = useCallback((data) => data.path, []);

    return (
        <div
            className="ag-theme-alpine"
            style={{
                height: "600px",
                width: "1000px",
                margin: "20px auto",
                fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
                fontSize: 14,
            }}
        >
            <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                treeData={true}
                getDataPath={getDataPath}
                autoGroupColumnDef={autoGroupColumnDef}
                groupDefaultExpanded={-1} // expand all
                animateRows={true}
                suppressRowClickSelection={true}
                domLayout="autoHeight"
            />
        </div>
    );
}
